import { v4 as isUUID } from 'is-uuid'
import { Fragment, Slice } from 'prosemirror-model'
import { Plugin, PluginKey } from 'prosemirror-state'
import { v4 as uuid } from 'uuid'

const idMakerKey = new PluginKey('ID_MAKER')
/**
 * Makes sure every block always has ID.
 * @returns {Plugin} new plugin instance
 */
function idMakerPlugin() {
  let lastAction = null

  return new Plugin({
    key: idMakerKey,
    appendTransaction(transactions, oldState, newState) {
      const last = transactions[transactions.length - 1]
      // exit early under the following conditions:
      // last transaction is from collab plugin
      // doc is unchanged
      if (last.getMeta('collab$') || oldState.doc.eq(newState.doc)) {
        return
      }

      const { tr, doc } = newState
      const ids = new Set()

      // some transactions may not change the document so need to check each one
      for (let i = 0; i < transactions.length; i++) {
        // changedRange gives the range in transaction[i].doc that was changed
        const changed = transactions[i].changedRange()
        if (!changed) {
          continue
        }
        // Map positions through subsequent transactions to get positions in newState.doc
        let { from, to } = changed
        for (let j = i + 1; j < transactions.length; j++) {
          from = transactions[j].mapping.map(from)
          to = transactions[j].mapping.map(to)
        }
        doc.nodesBetween(from, to, (node, pos) => {
          if (node.type.isInGroup('block')) {
            // check for ID support
            // NOTE: spec.attrs can be undefined (dual_dialogue_column)
            const blockSupportsIDs = Boolean(node.type.spec.attrs?.id)
            if (!blockSupportsIDs) {
              return
            }
            // verify new ID is needed
            // non-uuid ids snuck in before we landed
            // https://github.com/showrunner/codex/pull/92
            // the second check (lazily) cleans up the original mess.
            // given how hot this codepath is, we might run a DB query
            // in a month or two to see if its still needed.
            const blockHasID = isUUID(node.attrs.id)

            const isDuplicateID = blockHasID && ids.has(node.attrs.id)
            if (blockHasID && !isDuplicateID) {
              ids.add(node.attrs.id) // register ID for loop comparison
              if (node.isTextblock) {
                return false
              }
              return // return undefined for dual_dialogue to allow descending further
            }
            // generate new ID
            const attrs = { ...node.attrs }
            attrs.id = uuid()
            ids.add(attrs.id) // register ID for loop comparison
            // second arg is node type, setting to undefined ensures it won't be changed
            tr.setNodeMarkup(pos, null, attrs)
            // don't descend into inline nodes
            if (node.isTextblock) {
              return false
            }
          }
        })
      }
      if (tr.docChanged) {
        return tr
      }
    },
    props: {
      handleDOMEvents: {
        cut: () => {
          lastAction = 'cut'
          return false
        },
        copy: () => {
          lastAction = 'copy'
          return false
        },
        dragstart: () => {
          lastAction = 'dragstart'
          return false
        },
      },
      transformCopied: (slice) => {
        // On cut or drag, retain ids as is (to allow proper pasting within the doc)
        // On copy, strip ids to avoid duplicates when pasting
        if (lastAction === 'copy') {
          const newFragment = stripIdsFromFragment(slice.content)
          return new Slice(newFragment, slice.openStart, slice.openEnd)
        }
        lastAction = null
        return slice
      },
    },
  })
}

function stripIdsFromFragment(fragment) {
  const nodes = []

  fragment.forEach((child) => {
    let node = child

    // Recurse into children first
    if (child.content && child.content.size) {
      const newContent = stripIdsFromFragment(child.content)
      node = child.copy(newContent)
    }

    // Strip id-related attrs
    if (node.attrs?.id) {
      const newAttrs = { ...node.attrs }
      delete newAttrs.id
      node = node.type.create(newAttrs, node.content, node.marks)
    }

    nodes.push(node)
  })

  return Fragment.fromArray(nodes)
}

export { idMakerPlugin }
